/**
 * ERP & Treuhandbüro Data Portability / Connector Module (`web/src/lib/hooks.ts`).
 *
 * @statutory OR Art. 957a Abs. 2 Ziff. 1–5 (Vollständige & nachprüfbare Buchführung) · GeBüV Art. 6–10 (Datenübernahme & Prüfspur) · MWSTG Art. 70 · Schweizer Kontenrahmen KMU
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { api } from './api';
import { useApp } from './store';

/** Data hook with real states: loading, error (+ retry), data. Refetches when the app version bumps. */
export function useApi<T>(url: string | null, deps: unknown[] = []) {
  const { version } = useApp();
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(!!url);
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!url) return;
    let alive = true;
    setLoading(true); setError(null);
    api.get<T>(url).then((d) => { if (alive) { setData(d); setLoading(false); } }).catch((e) => { if (alive) { setError(e.message); setLoading(false); } });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, version, n, ...deps]);
  const reload = useCallback(() => setN((x) => x + 1), []);
  return { data, error, loading, reload, setData };
}

/** Focus after layout (two frames), caret at the end, no scroll jump. */
export function useAutofocus<T extends HTMLInputElement | HTMLTextAreaElement>(enabled = true) {
  const ref = useRef<T>(null);
  useLayoutEffect(() => {
    if (!enabled) return;
    let a = 0, b = 0;
    a = requestAnimationFrame(() => { b = requestAnimationFrame(() => {
      const el = ref.current;
      if (!el || (modalOpen() && !el.closest('[aria-modal="true"]'))) return;
      el.focus({ preventScroll: true });
      const len = el.value?.length ?? 0;
      try { el.setSelectionRange(len, len); } catch { /* not all inputs support it */ }
    }); });
    return () => { cancelAnimationFrame(a); cancelAnimationFrame(b); };
  }, [enabled]);
  return ref;
}

export function useKey(handler: (e: KeyboardEvent) => void, deps: unknown[] = []) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => handler(e);
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/** An open dialog is one that is rendered — a closed tour card stays in the DOM, hidden. */
export const modalOpen = (extra = '') => [...document.querySelectorAll<HTMLElement>(`[aria-modal="true"]${extra ? `, ${extra}` : ''}`)].some((m) => m.getClientRects().length > 0);

export const isTyping = () => {
  const el = document.activeElement as HTMLElement | null;
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
};
